import { useCallback, useEffect, useRef, type MouseEvent as ReactMouseEvent, type PointerEvent as ReactPointerEvent } from "react";

/** How long a finger must rest before the menu opens (iOS / Android use about half a second). */
export const LONG_PRESS_MS = 550;
/** Finger travel that turns a press into a scroll or a drag and cancels it. */
export const LONG_PRESS_SLOP = 8;
/** The click a browser may still dispatch after a long press is swallowed within this window. */
export const LONG_PRESS_CLICK_WINDOW_MS = 800;

export type LongPressPoint = { x: number; y: number };

/**
 * A long press with a finger or pen does what a right click does. Safari on iPad never fires
 * `contextmenu` for a long press, so a menu that only listens to it cannot be reached on a tablet
 * (CLAUDE.md hard rule 12); the mouse keeps its real right click. Moving the finger cancels it
 * (the tree scrolls, a marker drags). The click that follows a long press is swallowed, so the
 * row under the finger does not also get selected.
 *
 * Usage: `const longPress = useLongPress(onMenu)` once, then `{...longPress(item)}` on each element.
 */
export function useLongPress<T>(onLongPress: ((item: T, point: LongPressPoint) => void) | undefined) {
  const press = useRef<{ pointerId: number; x: number; y: number; timer: number } | null>(null);
  const firedAt = useRef(0);

  const cancel = useCallback(() => {
    if (press.current) {
      window.clearTimeout(press.current.timer);
      press.current = null;
    }
  }, []);
  useEffect(() => cancel, [cancel]);

  return useCallback((item: T) => ({
    onPointerDown: (e: ReactPointerEvent) => {
      cancel();
      if (!onLongPress || e.pointerType === "mouse") return;
      const { pointerId, clientX: x, clientY: y } = e;
      press.current = {
        pointerId,
        x,
        y,
        timer: window.setTimeout(() => {
          press.current = null;
          firedAt.current = Date.now();
          onLongPress(item, { x, y });
        }, LONG_PRESS_MS),
      };
    },
    onPointerMove: (e: ReactPointerEvent) => {
      const p = press.current;
      if (p && p.pointerId === e.pointerId && Math.hypot(e.clientX - p.x, e.clientY - p.y) > LONG_PRESS_SLOP) cancel();
    },
    onPointerUp: cancel,
    onPointerCancel: cancel,
    onClickCapture: (e: ReactMouseEvent) => {
      if (Date.now() - firedAt.current < LONG_PRESS_CLICK_WINDOW_MS) {
        e.preventDefault();
        e.stopPropagation();
      }
    },
  }), [cancel, onLongPress]);
}
